import reasonSection from '@/components/reasonSection.vue'
import confirm from '../../components/confirm/confirm.vue'
import progressDialog from '../../components/progressDialog.vue'
import LocationHelper from './Helpers/LocationHelper.js'
import MapService from './Services/MapService.js'
import AttendanceService from './Services/AttendanceService.js'
import CheckinApiHelper from './Helpers/CheckinApiHelper.js'

const ANDROID_PRECISE_LOCATION_GUIDE = 'https://scribehow.com/viewer/How_to_Enable_Precise_Location_Android__KgLz-XvkQJ6vXgWSrajJXA'
const IOS_PRECISE_LOCATION_GUIDE = 'https://scribehow.com/viewer/How_to_Enable_Precise_Location_iOS__XiX-SWIVREKclIwOm7bJNw'

export default {
  name: 'CheckIn',
  components: {
    reasonSection: reasonSection,
    progressDialog: progressDialog,
    confirm: confirm
  },
  data () {
    return {
      site: null,
      siteMatch: false,
      latestAttendance: null,
      loading: false,
      userLocation: null,
      mapService: null,
      attendanceService: null,
      retryPromise: {},
      retrycount: 0,
      message: null,
      waitingForLocation: false,
      locationFailed: false,
      checkinoutError: null,
      mapReady: false,
      retryCountdown: 0,
      retryTimer: null,
      retryAwaitingUser: false,
      processingCheckInOut: false,
      currentAttemptToken: 0,
      // site ID for the scanned QR code
      locationSiteId: null,
      tumblerId: null
    }
  },
  computed: {
    isCheckedIn () {
      return this.attendanceService.isCheckedIn(this.latestAttendance, this.siteMatch)
    },
    isIOSDevice () {
      return this.$store.getters.getIsIOS
    },
    siteParamId () {
      return this.$route.params.siteId
    },
    statusQuery () {
      return this.$route.query.status
    },
    supportGuideLink () {
      console.log('isIOSDevice', this.isIOSDevice)
      return this.isIOSDevice ? IOS_PRECISE_LOCATION_GUIDE : ANDROID_PRECISE_LOCATION_GUIDE
    }
  },
  methods: {
    openAccuracyTest () {
      window.open('/accuracytest.html', '_blank')
    },
    detectIOSDevice () {
      if (typeof navigator === 'undefined') {
        this.$store.commit('setIsIOS', false)
        return
      }
      const userAgent = navigator.userAgent || ''
      const hasTouchPoints = typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1
      const isiOS = /iPad|iPhone|iPod/.test(userAgent) || (userAgent.includes('Mac OS') && hasTouchPoints)
      this.$store.commit('setIsIOS', isiOS)
    },
    async getSite (siteId, shouldShowOnMap = true) {
      try {
        this.site = await this.attendanceService.getSite(siteId)
        if (this.site && shouldShowOnMap) {
          this.mapService.updateSiteLayer(this.site)
        }
      } catch (err) {
        this.$rollbar.error('getSite failed', err, { siteId })
        this.$snotify.error(err.message)
      }
    },
    async getLatestAttendance () {
      this.loading = true
      try {
        const attendance = await this.attendanceService.getLatestAttendance()
        if (attendance) {
          this.siteMatch = this.attendanceService.doesAttendanceMatchSite(
            attendance,
            this.locationSiteId
          )
          // If user is checked in (has checkInTime but no checkOutTime), show the site
          if (attendance.checkInTime != null && attendance.checkOutTime == null) {
            await this.getSite(attendance.jobId, true)
          }
          this.latestAttendance = attendance
        } else {
          this.latestAttendance = null
        }
      } catch (err) {
        this.$snotify.error(err.message)
      } finally {
        this.loading = false
      }
    },

    async getSimpleUserLocation () {
      try {
        const pos = await LocationHelper.getSimpleLocation()
        this.userLocation = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy
        }
        this.mapService.updateUserMarker(this.userLocation)
        this.mapService.fitToBounds()
        this.locationFailed = false
        // Clear waiting flag once we have initial location
        this.waitingForLocation = false
      } catch (err) {
        this.$rollbar.warning('getSimpleUserLocation failed', err)

        this.locationFailed = true
        // Still clear the flag so user sees the error alert instead of spinner
        this.waitingForLocation = false
        throw err
      }
    },

    async tryGetBetterLocation (attempt = 1, maxAttempts = 3) {
      // Generate new attempt token to guard against stale callbacks
      const attemptToken = ++this.currentAttemptToken

      // Start countdown timer
      this.retryAwaitingUser = false
      this.retryCountdown = 10
      if (this.retryTimer) clearInterval(this.retryTimer)
      this.retryTimer = setInterval(() => {
        // Guard: only update if this is still the current attempt
        if (this.currentAttemptToken !== attemptToken) return

        this.retryCountdown--
        if (this.retryCountdown <= 0) {
          this.retryCountdown = 0
          this.retryAwaitingUser = true
          this.waitingForLocation = false
          clearInterval(this.retryTimer)
          this.retryTimer = null
        }
      }, 1000)

      // Once the helper signals completion we can treat the retry as resolved immediately
      this.getPreciseUserLocation((result = {}) => {
        // Guard: only resolve if this is still the current attempt
        if (this.currentAttemptToken !== attemptToken) return

        if (result.error) {
          if (this.retryPromise.resolve) {
            this.retryPromise.resolve({ error: true })
          }
        } else if (this.retryPromise.resolve) {
          this.retryPromise.resolve({ success: true })
        }
        // Clear timer when location is found
        if (this.retryTimer) {
          clearInterval(this.retryTimer)
          this.retryTimer = null
        }
        this.retryCountdown = 0
        this.waitingForLocation = false
      })
    },

    handleRetryPrompt () {
      // Invalidate current attempt to prevent stale callbacks
      this.currentAttemptToken++

      // Stop countdown
      if (this.retryTimer) {
        clearInterval(this.retryTimer)
        this.retryTimer = null
      }

      // Resolve the appropriate promise based on current state
      if (this.retryAwaitingUser) {
        // Attempt 2: Dialog is open, resolve reasonSection promise
        // Use $nextTick for iOS Safari to ensure DOM updates complete before promise resolution
        this.$nextTick(() => {
          if (this.$refs.reasonSection && this.$refs.reasonSection.resolve) {
            this.$refs.reasonSection.resolve({ reason: null, retry: true })
          }
        })
      } else {
        // Attempt 1: Countdown is running, resolve retryPromise
        if (this.retryPromise.resolve) {
          this.retryPromise.resolve({ retry: true })
        }
      }
    },
    async getPreciseUserLocation (samplingCompleteCallback = null, windowMs = 15000) {
      try {
        await LocationHelper.getGoodFix({
          windowMs: windowMs,
          updateCallback: (pos, finalSample) => {
            this.locationFailed = false
            const newLocation = {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy: pos.coords.accuracy
            }
            // Only update if it's better accuracy or first precise fix
            if (!this.userLocation || pos.coords.accuracy < this.userLocation.accuracy) {
              this.userLocation = newLocation
              this.mapService.updateUserMarker(this.userLocation)
              this.mapService.fitToBounds()
            }
            if (finalSample && samplingCompleteCallback) {
              samplingCompleteCallback({ success: true })
            }
          }
        })
      } catch (err) {
        // Don't set locationFailed here - simple location might have worked
        // Just call the callback to proceed
        if (samplingCompleteCallback) {
          samplingCompleteCallback({ error: true, message: err.message })
        }
      }
    },
    /**
     * perform the check out
     * @param {GUID} siteId we are checking out of
     * @param {bool} manual false if scanned
     * @param {string} secret hashed date and time the QR was generated
     */
    async handleManualCheckOut () {
      const confirm = await this.$refs.confirm.open('Checkout', 'Are you sure you want to checkout', { color: 'primary', confirm: 'Yes', timeout: 10 })

      if (confirm) {
        this.performManualCheckOutFlow()
      }
    },

    async shouldWeCheckinOrOutFlow () {
      this.$refs.reasonSection.reset()
      this.$nextTick(async () => {
        if (this.latestAttendance != null && this.latestAttendance.checkOutTime == null) {
          this.waitingForLocation = true
          await this.performCheckOutFlow()
        } else if (this.latestAttendance == null || this.latestAttendance.checkOutTime != null) {
          this.waitingForLocation = true
          await this.performCheckInFlow()
        }
      })
    },

    async performCheckInFlow (attempt = 1) {
      if (this.latestAttendance != null && this.latestAttendance.checkOutTime == null) {
        this.waitingForLocation = false
        this.checkinoutError = 'You are already checked in'
        return
      }

      if (this.site == null) {
        this.$rollbar.warning('performCheckInFlow: site is null', {
          locationSiteId: this.locationSiteId,
          siteParamId: this.$route.params.siteId,
          latestAttendance: this.latestAttendance,
          userLocation: this.userLocation,
          routePath: this.$route.fullPath
        })
        this.waitingForLocation = false
        this.checkinoutError = 'Site information is missing. Cannot proceed with checkin.'
        return
      }

      if (this.userLocation == null) {
        this.$rollbar.warning('performCheckInFlow: user location is null')
        this.waitingForLocation = false
        this.checkinoutError = 'Location information is missing. Cannot proceed with checkin.'
        return
      }

      if (this.userLocation.latitude == null || this.userLocation.longitude == null) {
        this.$rollbar.warning('performCheckInFlow: invalid location coordinates', { user: this.userLocation })
        this.waitingForLocation = false
        this.checkinoutError = 'Location data is incomplete. Cannot proceed with checkin.'
        return
      }

      if (this.site == null) {
        this.$rollbar.warning('performCheckInFlow: site is null', {
          locationSiteId: this.locationSiteId,
          siteParamId: this.$route.params.siteId
        })
        this.waitingForLocation = false
        this.checkinoutError = 'Site information is missing. Cannot proceed with checkin.'
        return
      }

      if (this.site.latitude == null || this.site.longitude == null) {
        this.$rollbar.warning('performCheckInFlow: invalid site location', { center: this.site })
        this.waitingForLocation = false
        this.checkinoutError = 'Site location data is incomplete. Cannot proceed with checkin.'
        return
      }

      const distance = LocationHelper.distanceToSite(
        this.userLocation, { latitude: this.site.latitude, longitude: this.site.longitude }
      )

      if (distance > this.site.radius) {
        if (attempt < 2) {
          this.waitingForLocation = false
          this.tryGetBetterLocation()
          this.retryPromise = {}
          const retryResult = await new Promise((resolve, reject) => {
            this.retryPromise.resolve = resolve
            this.retryPromise.reject = reject
          })

          // If user clicked retry during countdown, restart from attempt 1
          if (retryResult && retryResult.retry) {
            await this.performCheckInFlow(1)
            return
          }

          await this.performCheckInFlow(attempt + 1)
          return
        } else {
          // Show reason dialog - hide progress bar but keep retry section
          this.retryAwaitingUser = true
          const supportLink = this.isIOSDevice ? IOS_PRECISE_LOCATION_GUIDE : ANDROID_PRECISE_LOCATION_GUIDE
          var response = await this.$refs.reasonSection.open(
            'Off-Site Check-In:',
            `You are outside the site radius. Please enter a reason for checking in off-site and ensure you have precise location enabled in your device settings.<br>For more instructions visit <a href="${supportLink}" target="_blank" rel="noopener" style="color: #1976d2; text-decoration: underline;">this step-by-step guide</a>`
          )
          this.lastGeoReason = response

          // If user clicked Retry, restart from attempt 1
          if (response && response.retry) {
            await this.performCheckInFlow(1)
            return
          }

          // If no response (user cancelled), don't proceed
          if (!response || !response.reason) {
            this.retryPromise = {} // Clear retry UI when cancelled
            return
          }

          // Clear retry UI when proceeding with reason
          this.retryPromise = {}
        }
      } else {
        // Inside radius - clear retry UI
        this.retryPromise = {}
      }
      this.waitingForLocation = false
      this.processingCheckInOut = true
      await this.checkIn(this.locationSiteId)
      await this.getLatestAttendance()
      this.processingCheckInOut = false
    },

    async performCheckOutFlow (attempt = 1) {
      if (this.latestAttendance == null || this.latestAttendance.checkInTime == null) {
        this.waitingForLocation = false
        this.checkinoutError = 'You are not currently checked in.'
        return
      }

      if (this.site == null) {
        this.$rollbar.warning('performCheckOutFlow: site is null', {
          locationSiteId: this.locationSiteId,
          siteParamId: this.$route.params.siteId,
          latestAttendance: this.latestAttendance,
          userLocation: this.userLocation,
          routePath: this.$route.fullPath
        })
        this.waitingForLocation = false
        this.checkinoutError = 'Site information is missing. Cannot proceed with checkout.'
        return
      }

      if (this.siteMatch === false) {
        this.waitingForLocation = false
        this.checkinoutError = 'You are checked in at a different site. Please check out from that site first.'
        return
      }

      if (this.userLocation == null) {
        this.$rollbar.warning('performCheckOutFlow: user location is null')
        this.waitingForLocation = false
        this.checkinoutError = 'Location information is missing. Cannot proceed with checkout.'
        return
      }

      if (this.userLocation.latitude == null || this.userLocation.longitude == null) {
        this.$rollbar.warning('performCheckOutFlow: invalid location coordinates', { user: this.userLocation })
        this.waitingForLocation = false
        this.checkinoutError = 'Location data is incomplete. Cannot proceed with checkout.'
        return
      }

      if (this.site == null) {
        this.$rollbar.warning('performCheckOutFlow: site is null', {
          locationSiteId: this.locationSiteId,
          siteParamId: this.$route.params.siteId
        })
        this.waitingForLocation = false
        this.checkinoutError = 'Site information is missing. Cannot proceed with checkout.'
        return
      }

      if (this.site.latitude == null || this.site.longitude == null) {
        this.$rollbar.warning('performCheckOutFlow: invalid site location', { center: this.site })
        this.waitingForLocation = false
        this.checkinoutError = 'Site location data is incomplete. Cannot proceed with checkout.'
        return
      }

      const distance = LocationHelper.distanceToSite(
        this.userLocation, { latitude: this.site.latitude, longitude: this.site.longitude }
      )

      if (distance > this.site.radius) {
        if (attempt < 2) {
          this.waitingForLocation = false
          this.tryGetBetterLocation()
          this.retryPromise = {}
          const retryResult = await new Promise((resolve, reject) => {
            this.retryPromise.resolve = resolve
            this.retryPromise.reject = reject
          })

          // If user clicked retry during countdown, restart from attempt 1
          if (retryResult && retryResult.retry) {
            await this.performCheckOutFlow(1)
            return
          }

          await this.performCheckOutFlow(attempt + 1)
          return
        } else {
          // Show reason dialog - hide progress bar but keep retry section
          this.retryAwaitingUser = true
          const supportLink = this.isIOSDevice ? IOS_PRECISE_LOCATION_GUIDE : ANDROID_PRECISE_LOCATION_GUIDE
          var response = await this.$refs.reasonSection.open(
            `Checking Out: ${this.site.name}`,
            `You are outside the site radius. Please enter a reason for checking out off site and ensure you have precise location enabled in your device settings.<br>For more instructions visit <a href="${supportLink}" target="_blank" rel="noopener" style="color: #1976d2; text-decoration: underline;">this step-by-step guide</a>`
          )
          this.lastGeoReason = response
          this.waitingForLocation = false

          // If user clicked Retry, restart from attempt 1
          if (response && response.retry) {
            await this.performCheckOutFlow(1)
            return
          }

          // If no response (user cancelled), don't proceed
          if (!response || !response.reason) {
            this.retryPromise = {} // Clear retry UI when cancelled
            return
          }

          // Clear retry UI when proceeding with reason
          this.retryPromise = {}
        }
      } else {
        // Inside radius - clear retry UI
        this.retryPromise = {}
      }

      this.processingCheckInOut = true
      await this.checkOut(this.locationSiteId)
      await this.getLatestAttendance()
      this.processingCheckInOut = false
    },

    async performManualCheckOutFlow () {
      if (this.latestAttendance == null || this.latestAttendance.checkInTime == null) {
        this.waitingForLocation = false
        this.checkinoutError = 'You are not currently checked in.'
        return
      }

      // Scroll to reason section
      this.$nextTick(() => {
        if (this.$refs.reasonSection && this.$refs.reasonSection.$el) {
          this.$refs.reasonSection.$el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })

      var response = await this.$refs.reasonSection.open(
        'Check-Out',
        'You are checking out without a QR code. Please enter a reason for checking out off-site.'
      )

      if (!response || !response.reason) {
        this.waitingForLocation = false
        this.checkinoutError = 'Checkout cancelled. Please select a reason to proceed.'
        return
      }

      this.lastGeoReason = response
      this.waitingForLocation = false

      await this.checkOut(this.latestAttendance.jobId, true)
      await this.getLatestAttendance()
      this.message = 'You have successfully checked out.'
      location.reload()
    },

    async checkOut (siteId, manual = false) {
      let secret = ''
      if (this.$route.params.secret != null) {
        secret = this.$route.params.secret
      }

      const params = {
        checkInTime: null,
        checkInLatitude: null,
        checkInLongitude: null,
        checkOutTime: null,
        checkOutLatitude: this.userLocation.latitude,
        checkOutLongitude: this.userLocation.longitude,
        jobId: siteId,
        tumblerId: this.tumblerId,
        comment: this.lastGeoReason && this.lastGeoReason.reason ? this.lastGeoReason.reason : null,
        qRCheckOut: !manual,
        qRCheckIn: false,
        fitForWork: '',
        secret: secret
      }

      try {
        await CheckinApiHelper.checkOut(params)
        this.message = 'You have successfully checked out.'
        this.$router.replace({ name: 'CheckIn', query: { status: 'success' } })
      } catch (err) {
        this.$rollbar.error('checkOut failed', err, { siteId, manual })
        this.message = null
        this.checkinoutError = `Checkout failed: ${err.message}`
        this.$router.replace({ name: 'CheckIn', query: { status: 'failed' } })
      }
    },

    /**
     * Submit the tumbler power reading carried in the scanned QR URL, if present.
     *
     * Fire-and-forget by design. Sends only when BOTH power parameters are
     * present: their absence means the device's firmware predates power
     * reporting, which must produce silence rather than a warning. Their
     * presence carrying 'n' means the firmware reports but its UPS did not
     * answer, which is a fault the server needs to hear about — so 'n' is
     * forwarded, not filtered out here.
     *
     * The QR payload-contract version ('v') is forwarded as an optional extra
     * field whenever present, alongside the power values — it is never
     * required and never parsed or classified here; the server does that.
     *
     * The RTC reset flag ('rtc') is likewise forwarded as an optional extra
     * field whenever present — it is never required and never parsed or
     * classified here; the server does that.
     *
     * The route's QR secret is forwarded whenever present so the server can
     * reject readings from a stale QR (refresh, bookmark, photo of an old
     * code) with the same freshness check it applies to check-ins.
     *
     * Every failure is swallowed. A telemetry problem is not a check-in problem
     * and must never reach the worker or delay the interface.
     *
     * @returns {void}
     */
    postTumblerTelemetry () {
      const batLvl = this.$route.query.batLvl
      const pwrSrc = this.$route.query.pwrSrc
      const qrPayloadVersion = this.$route.query.v
      const rtc = this.$route.query.rtc
      const secret = this.$route.params.secret
      const tumblerId = this.$route.params.siteId

      if (tumblerId == null || batLvl == null || pwrSrc == null) {
        return
      }

      const payload = {
        tumblerId: tumblerId,
        batteryLevel: String(batLvl),
        powerSource: String(pwrSrc)
      }

      if (qrPayloadVersion != null) {
        payload.qrPayloadVersion = String(qrPayloadVersion)
      }

      if (rtc != null) {
        payload.rtc = String(rtc)
      }

      if (secret != null) {
        payload.secret = String(secret)
      }

      this.attendanceService
        .postTelemetry(payload)
        .catch(() => {
          // Intentionally silent — see method remarks.
        })
    },

    async checkIn (siteId) {
      let secret = ''
      if (this.$route.params.secret != null) {
        secret = this.$route.params.secret
      }

      const params = {
        checkInTime: null,
        checkInLatitude: this.userLocation.latitude,
        checkInLongitude: this.userLocation.longitude,
        checkOutTime: null,
        checkOutLatitude: null,
        checkOutLongitude: null,
        jobId: siteId,
        tumblerId: this.tumblerId,
        comment: this.lastGeoReason && this.lastGeoReason.reason ? this.lastGeoReason.reason : null,
        qRCheckOut: false,
        qRCheckIn: true,
        fitForWork: '',
        secret: secret
      }

      try {
        await CheckinApiHelper.checkIn(params)
        this.message = 'You have successfully checked in.'
        this.$router.replace({ name: 'CheckIn', query: { status: 'success' } })
      } catch (err) {
        this.$rollbar.error('checkIn failed', err, { siteId })
        this.message = null
        this.checkinoutError = (`Checkin failed: ${err.message}`)
        this.$router.replace({ name: 'CheckIn', query: { status: 'failed' } })
      }
    }
  },
  created () {
    this.mapService = new MapService()
    this.attendanceService = new AttendanceService()
  },
  /**
   * Vue lifecycle hook. Initialises services, submits any tumbler power reading
   * carried in the scanned URL, then builds the map, resolves the user and the
   * site, and starts location acquisition.
   *
   * Side effects: creates the map, calls the check-in API, posts telemetry, and
   * reports failures to Rollbar.
   *
   * @returns {Promise<void>}
   */
  async mounted () {
    // Initialize services
    this.detectIOSDevice()
    this.mapService = new MapService()
    this.attendanceService = new AttendanceService()

    // Submit the tumbler power reading FIRST, before anything that can throw.
    //
    // This ordering is load-bearing, not stylistic. getTumblerSiteId below throws
    // for tumblers with no active job assignment and for those with more than one
    // — precisely the devices whose readings must still be recorded. Posting after
    // it would silently exclude them. It needs only the identifier already in the
    // URL, so it has no reason to wait for anything.
    this.postTumblerTelemetry()

    try {
      this.waitingForLocation = true
      // Create map
      await this.mapService.createMap(this.$refs.siteMap)
      this.mapReady = true

      // Initialize user and load data
      const user = await this.attendanceService.initializeUser()

      if (user && user.appUser) {
        this.$rollbar.configure({
          payload: {
            person: {
              id: user.appUser.id,
              username: user.appUser.userName,
              email: user.appUser.email
            }
          }
        })
      }
      await this.getLatestAttendance()
    } catch (err) {
      this.$rollbar.error('CheckIn mounted: initialization failed', err, {
        locationSiteId: this.locationSiteId,
        siteParamId: this.$route.params.siteId,
        siteLoaded: this.site != null,
        routePath: this.$route.fullPath
      })
      this.$snotify.error(err.message)
    }

    try {
      if (this.$route.params.siteId != null) {
        this.locationSiteId = await this.attendanceService.getTumblerSiteId(this.$route.params.siteId)
        this.tumblerId = this.$route.params.siteId
        if (this.locationSiteId != null) {
          await this.getSite(this.locationSiteId)
        }
      }
    } catch (err) {
      this.$rollbar.error('CheckIn mounted: initialization failed', err, {
        locationSiteId: this.locationSiteId,
        siteParamId: this.$route.params.siteId,
        siteLoaded: this.site != null,
        routePath: this.$route.fullPath
      })
      this.$snotify.error(err.message)
    }

    // Get user location (parallel - both start immediately)
    // Simple location (fast, low accuracy) runs first
    this.getSimpleUserLocation()
      .then(() => {
        // Once simple location succeeds, trigger check-in flow if needed
        if (this.locationSiteId != null && !this.locationFailed && this.userLocation != null) {
          this.shouldWeCheckinOrOutFlow()
        }
      })
      .catch((err) => {
        this.$rollbar.error('Location acquisition failed', err, {
          siteId: this.locationSiteId,
          siteParamId: this.$route.params.siteId
        })
        this.checkinoutError = 'Unable to get location. Please enable location permissions and try again.'
        this.locationFailed = true
        this.waitingForLocation = false
      })

    // Precise location (10s sampling, high accuracy) refines in parallel
    this.getPreciseUserLocation(() => {
      // This callback fires when precise sampling completes
      this.waitingForLocation = false
    }, 10000)
  },
  beforeDestroy () {
    // Clean up map resources
    if (this.mapService) {
      this.mapService.destroy()
    }
  }
}
